import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import TableActions from "../../components/TableActions";
import ActionButton from "../../components/ActionButton";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { graphqlEndpoint } from "../../config";
import {
  READ_PUBLIC_HOLIDAYS,
} from "../../graph/holidayManagement/queries";
import {
  DELETE_PUBLIC_HOLIDAY,
  UPDATE_PUBLIC_HOLIDAY,
} from "../../graph/holidayManagement/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const jurisdictions = [
  "ENGLAND",
  "WALES",
  "SCOTLAND",
  "NORTHERN_IRELAND",
];

const holidayValidationSchema = yup.object({
  jurisdiction: yup.string().oneOf(jurisdictions).required("Select a jurisdiction"),
  name: yup.string().trim().required("Holiday name is required"),
  date: yup.string().required("Holiday date is required"),
  observedDate: yup.string().nullable(),
  year: yup
    .number()
    .integer()
    .required("Year is required"),
  source: yup.string().nullable(),
});

const ManagePublicHolidays = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();

  const [jurisdiction, setJurisdiction] = useState(
    location.state?.jurisdiction || "WALES"
  );
  const [year, setYear] = useState(
    location.state?.year || new Date().getFullYear()
  );
  const [rows, setRows] = useState([]);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const data = await client.request(READ_PUBLIC_HOLIDAYS, {
        jurisdiction,
        year: Number(year),
      });

      setRows(data.readPublicHolidays || []);
    } catch (err) {
      console.error("Failed to fetch public holidays:", err);
      setRows([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load public holidays",
      });
    }
  }, [jurisdiction, year, setSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenEditDialog = (holiday) => {
    setSelectedHoliday(holiday);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedHoliday(null);
  };

  const handleOpenDeleteDialog = (holiday) => {
    setSelectedHoliday(holiday);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedHoliday(null);
  };

  const handleUpdateHoliday = async (values, { setSubmitting }) => {
    if (!selectedHoliday?.id) return;

    const input = {
      id: selectedHoliday.id,
      jurisdiction: values.jurisdiction,
      name: values.name.trim(),
      date: values.date,
      observedDate: values.observedDate || null,
      year: Number(values.year),
      source: values.source?.trim() || null,
    };

    try {
      await client.request(UPDATE_PUBLIC_HOLIDAY, { input });

      handleCloseEditDialog();
      await fetchData();

      setSnackbar({
        open: true,
        severity: "success",
        message: "Public holiday updated successfully",
      });
    } catch (err) {
      console.error("Failed to update public holiday:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update public holiday",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedHoliday?.id) return;

    try {
      const data = await client.request(DELETE_PUBLIC_HOLIDAY, {
        id: selectedHoliday.id,
      });

      if (!data.deletePublicHoliday) {
        throw new Error("The public holiday was not deleted");
      }

      handleCloseDeleteDialog();
      await fetchData();

      setSnackbar({
        open: true,
        severity: "success",
        message: "Public holiday deleted successfully",
      });
    } catch (err) {
      console.error("Failed to delete public holiday:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete public holiday",
      });
    }
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "jurisdiction", headerName: "Jurisdiction", flex: 1 },
    { field: "date", headerName: "Date", flex: 1 },
    { field: "observedDate", headerName: "Observed Date", flex: 1 },
    { field: "year", headerName: "Year", width: 100 },
    { field: "source", headerName: "Source", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TableActions
          onEdit={() => handleOpenEditDialog(row)}
          onDelete={() => handleOpenDeleteDialog(row)}
          hideCreate
        />
      ),
    },
  ];

  const editInitialValues = selectedHoliday
    ? {
        jurisdiction: selectedHoliday.jurisdiction || "WALES",
        name: selectedHoliday.name || "",
        date: selectedHoliday.date || "",
        observedDate: selectedHoliday.observedDate || "",
        year: selectedHoliday.year || year,
        source: selectedHoliday.source || "",
      }
    : {
        jurisdiction,
        name: "",
        date: "",
        observedDate: "",
        year,
        source: "",
      };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="PUBLIC HOLIDAYS"
          subtitle="Manage public holidays by jurisdiction and year"
        />

        <ActionButton
          onClick={() =>
            navigate("/createPublicHoliday", {
              state: { jurisdiction, year },
            })
          }
          tone="gold"
        >
          Create Public Holiday
        </ActionButton>
      </Box>

      <Box display="flex" gap={2} mt={3} mb={2}>
        <TextField
          select
          label="Jurisdiction"
          value={jurisdiction}
          onChange={(event) => setJurisdiction(event.target.value)}
          sx={{ minWidth: 220 }}
        >
          {jurisdictions.map((value) => (
            <MenuItem key={value} value={value}>
              {value.replace("_", " ")}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Year"
          type="number"
          value={year}
          onChange={(event) => setYear(event.target.value)}
          sx={{ width: 140 }}
        />
      </Box>

      <ThemedDataGrid rows={rows} columns={columns} />

      <Formik
        key={selectedHoliday?.id || "no-holiday-selected"}
        initialValues={editInitialValues}
        validationSchema={holidayValidationSchema}
        onSubmit={handleUpdateHoliday}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <Dialog
            open={editDialogOpen}
            onClose={handleCloseEditDialog}
            fullWidth
            maxWidth="sm"
          >
            <form onSubmit={handleSubmit}>
              <DialogTitle>Edit Public Holiday</DialogTitle>

              <DialogContent>
                <Box display="grid" gap={2} mt={1}>
                  <TextField
                    select
                    label="Jurisdiction"
                    name="jurisdiction"
                    value={values.jurisdiction}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!touched.jurisdiction && !!errors.jurisdiction}
                    helperText={touched.jurisdiction && errors.jurisdiction}
                  >
                    {jurisdictions.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value.replace("_", " ")}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label="Name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                  />

                  <TextField
                    label="Date"
                    name="date"
                    type="date"
                    value={values.date}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    InputLabelProps={{ shrink: true }}
                    error={!!touched.date && !!errors.date}
                    helperText={touched.date && errors.date}
                  />

                  <TextField
                    label="Observed Date"
                    name="observedDate"
                    type="date"
                    value={values.observedDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    label="Year"
                    name="year"
                    type="number"
                    value={values.year}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!touched.year && !!errors.year}
                    helperText={touched.year && errors.year}
                  />

                  <TextField
                    label="Source"
                    name="source"
                    value={values.source}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Box>
              </DialogContent>

              <DialogActions>
                <ActionButton
                  type="button"
                  tone="secondary"
                  onClick={handleCloseEditDialog}
                >
                  Cancel
                </ActionButton>
                <ActionButton type="submit" tone="success" disabled={isSubmitting}>
                  Save Changes
                </ActionButton>
              </DialogActions>
            </form>
          </Dialog>
        )}
      </Formik>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Public Holiday</DialogTitle>
        <DialogContent>
          <Typography>
            Delete {selectedHoliday?.name || "this public holiday"}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <ActionButton
            type="button"
            tone="secondary"
            onClick={handleCloseDeleteDialog}
          >
            Cancel
          </ActionButton>
          <ActionButton
            type="button"
            tone="danger"
            onClick={handleConfirmDelete}
          >
            Delete
          </ActionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManagePublicHolidays;